/**
 * conv_share_v1: ตัวกลางดึง /api/conversations ตัวเดียวของทั้งแอป
 *
 * ปัญหาเดิม: Inbox กับ Layout ต่างคนต่างดึง /api/conversations ของตัวเอง
 *   - ทุกครั้งที่ SSE ส่ง conversation_updated ทั้งคู่ debounce 400ms เท่ากัน
 *     แล้วยิง request พร้อมกัน = 2 request ต่อ 1 เหตุการณ์
 *   - บวก poll ของใครของมัน (30 วิ + 60 วิ) = 3 request/นาที ตอนไม่มีอะไรเกิดขึ้นเลย
 *
 * วิธีแก้: ถ้ามี request ค้างอยู่ ให้เกาะอันเดิมแทนที่จะยิงใหม่
 *
 * หัวใจอยู่ที่ `minTime` — ผู้เรียกบอกว่า "ฉันต้องการข้อมูลที่เริ่มดึงตั้งแต่เวลานี้เป็นต้นไป"
 * เพื่อไม่ให้เผลอเสิร์ฟข้อมูลเก่ากว่าเหตุการณ์ที่เพิ่งเกิด:
 *   - poll ตามเวลา          -> minTime 0            (ข้อมูลสดๆ ในช่วง FRESH_MS พอ)
 *   - SSE บอกว่ามีอัปเดต     -> minTime = เวลาที่ event มาถึง
 *   - หลังกด รับเคส/ปิด/บล็อค -> minTime = Date.now() หลัง API ตอบกลับ (ห้ามใช้ของเก่าเด็ดขาด)
 */
import API from '../api';

/* ช่วงที่ถือว่าผลลัพธ์ยัง "สดพอ" สำหรับผู้เรียกที่ไม่ได้กำหนด minTime
   สั้นมากโดยตั้งใจ — มีไว้กันเคสที่หลาย component โหลดพร้อมกันตอน mount เท่านั้น */
const FRESH_MS = 1000;

let inflight = null;  // { startedAt, promise }
let last = null;      // { at, data }

export function fetchConversations(opts = {}) {
  const minTime = opts.minTime || 0;

  // มี request ที่ "เริ่มยิงหลังเวลาที่ผู้เรียกต้องการ" ค้างอยู่แล้ว -> เกาะอันนั้น
  if (inflight && inflight.startedAt >= minTime) return inflight.promise;

  // เพิ่งได้ผลมาหมาดๆ และใหม่พอสำหรับผู้เรียก -> ใช้ซ้ำ
  if (last && last.at >= minTime && Date.now() - last.at <= FRESH_MS) {
    return Promise.resolve(last.data);
  }

  const startedAt = Date.now();
  const req = { startedAt, promise: null };

  /* เคลียร์ช่อง in-flight ในจังหวะเดียวกับที่ผลลัพธ์มาถึง (ทั้งสำเร็จและพัง)
     ถ้าปล่อยให้เคลียร์ทีหลัง จะมีช่องให้ผู้เรียกรายถัดไปเกาะ request ที่จบไปแล้ว
     แล้วได้ข้อมูลเก่ากลับไปโดยไม่รู้ตัว */
  req.promise = API.get(`/api/conversations?_=${startedAt}`).then(
    (r) => {
      last = { at: startedAt, data: r.data };
      if (inflight === req) inflight = null;
      return r.data;
    },
    (err) => {
      if (inflight === req) inflight = null;
      throw err;
    }
  );

  inflight = req;
  return req.promise;
}

/* สำหรับเทสต์ / ตอน logout */
export function resetConversationsCache() {
  inflight = null;
  last = null;
}
